import React, { useState, useEffect, useRef } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useToast } from "./common/Toast";
import { useTx } from "../context/TxContext";
import { getDexContract, getTokenContract } from "../web3/contracts";
import { getEthersProvider } from "../web3/provider";
import { parseError } from "../utils/errors";
import { fmtUsdt, fmtUnits } from "../utils/format";
import { SELL_FEE_PCT, DEFAULT_SLIPPAGE, QUOTE_DEBOUNCE_MS } from "../config/app.config";
import Confetti from "./common/Confetti";
import "./SwapCard.css";

const SLIPPAGES = [0.5, 1, 2];

export default function SwapCard({ market, data, refresh }) {
  const { signer } = useWeb3();
  const toast = useToast();
  const { run } = useTx();

  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);   // { gross, fee, payout }
  const [quoting, setQuoting] = useState(false);
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE);
  const [burst, setBurst] = useState(0);
  const debounceRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // Reset input when switching token markets.
  useEffect(() => { setAmount(""); setQuote(null); }, [market.key]);

  const dec = market.decimals;
  let amountBN = null;
  let amountValid = false;
  try {
    if (amount && Number(amount) > 0) { amountBN = ethers.utils.parseUnits(amount, dec); amountValid = true; }
  } catch { amountValid = false; }

  // Debounced live quote (uses the market's quote method).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!amountValid) { setQuote(null); setQuoting(false); return; }
    setQuoting(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const dex = getDexContract(market.dex, getEthersProvider());
        const q = await dex[market.quoteMethod](amountBN);
        if (mounted.current) setQuote({ gross: q.grossUSDT, fee: q.fee, payout: q.usdtPayout });
      } catch {
        if (mounted.current) setQuote(null);
      } finally {
        if (mounted.current) setQuoting(false);
      }
    }, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [amount, market.key]);

  const tokenBal = data ? data.tokenBal : null;
  const allowance = data ? data.allowance : null;
  const price = data ? data.price : null;
  const noLiquidity = price && price.isZero();

  const overBalance = amountValid && tokenBal && amountBN.gt(tokenBal);
  const needsApprove = amountValid && allowance && allowance.lt(amountBN);
  const payoutZero = quote && quote.payout.isZero();

  // Slippage is INFO-ONLY (the contract no longer takes minUsdtOut).
  const minOut = quote ? quote.payout.mul(10000 - Math.round(slippage * 100)).div(10000) : null;

  const onMax = () => { if (tokenBal) setAmount(ethers.utils.formatUnits(tokenBal, dec)); };
  const sanitize = (v) => v.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

  const onApprove = async () => {
    if (!signer) return;
    try {
      await run(`Approving ${market.label}`, async () => {
        const token = getTokenContract(market.token, signer);
        const tx = await token.approve(market.dex, ethers.constants.MaxUint256);
        await tx.wait();
      });
      toast.success(`${market.label} approved. You can sell now.`);
      refresh && refresh();
    } catch (e) { toast.error(parseError(e)); }
  };

  const onSell = async () => {
    if (!signer || !amountValid) return;
    try {
      await run(`Swapping ${market.label} → USDT`, async () => {
        const dex = getDexContract(market.dex, signer);
        const tx = await dex.sell(amountBN);  // single argument (no minUsdtOut)
        await tx.wait();
      });
      setBurst(Date.now());
      toast.success(`Swapped! You received ~${quote ? fmtUsdt(quote.payout) : ""} USDT.`);
      setAmount(""); setQuote(null);
      refresh && refresh();
    } catch (e) { toast.error(parseError(e)); }
  };

  let label = "Enter an amount";
  let fn = null, disabled = true, cls = "btn-block";
  if (noLiquidity) { label = "Awaiting liquidity"; }
  else if (amountValid) {
    if (overBalance) label = `Insufficient ${market.label} balance`;
    else if (quoting || !quote) label = "Fetching price…";
    else if (payoutZero) label = "Amount too small";
    else if (needsApprove) { label = `Approve ${market.label}`; fn = onApprove; disabled = false; }
    else { label = `Swap ${market.label} → USDT`; fn = onSell; disabled = false; cls = "btn-block btn-success"; }
  }

  return (
    <div className="swap-card card fade-up">
      {burst > 0 && <Confetti key={burst} />}
      <div className="swap-head">
        <h2 className="card-title" style={{ margin: 0 }}>Swap</h2>
        <span className="swap-net muted">{market.label} → USDT</span>
      </div>

      {/* FROM */}
      <div className="token-box">
        <div className="token-box-top">
          <span className="muted">You sell</span>
          <span className="muted">
            Balance: {tokenBal ? fmtUnits(tokenBal, dec, 4) : "0"}{" "}
            <button className="max-btn" onClick={onMax}>MAX</button>
          </span>
        </div>
        <div className="token-row">
          <input
            className="token-input"
            placeholder="0.0"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(sanitize(e.target.value))}
          />
          <span className="token-chip"><span className="tk-ico brand">◈</span> {market.label}</span>
        </div>
      </div>

      <div className="swap-divider"><span className="swap-arrow">↓</span></div>

      {/* TO */}
      <div className="token-box">
        <div className="token-box-top">
          <span className="muted">You receive (after {SELL_FEE_PCT}% Swap fee)</span>
        </div>
        <div className="token-row">
          <div className="token-input out">
            {quoting ? <span className="muted">…</span> : quote ? fmtUsdt(quote.payout) : "0.0"}
          </div>
          <span className="token-chip"><span className="tk-ico usdt">$</span> USDT</span>
        </div>
      </div>

      {/* INFO */}
      <div className="swap-info">
        <div className="info-row">
          <span className="muted">Price</span>
          <span>
            {noLiquidity ? "—" : price
              ? `1 ${market.label} ≈ $${fmtUnits(price, 18, market.priceDecimals)}`
              : "—"}
          </span>
        </div>
        <div className="info-row">
          <span className="muted">Swap Fee ({SELL_FEE_PCT}%)</span>
          <span>{quote ? `${fmtUsdt(quote.fee)} USDT` : "—"}</span>
        </div>
        <div className="info-row">
          <span className="muted">Slippage</span>
          <span className="slip">
            {SLIPPAGES.map((s) => (
              <button key={s} className={`slip-btn ${slippage === s ? "active" : ""}`} onClick={() => setSlippage(s)}>{s}%</button>
            ))}
          </span>
        </div>
        <div className="info-row">
          <span className="muted">Min. received (est.)</span>
          <span>{quote && minOut ? `${fmtUsdt(minOut)} USDT` : "—"}</span>
        </div>
      </div>

      <button className={`btn ${cls}`} onClick={fn || undefined} disabled={disabled}>
        {label}
      </button>
    </div>
  );
}
